import { useCallback, useMemo, useState } from 'react';
import { HStack } from '@cad-arc';
import { EventPanelFormOpenProQaButtons } from './event-panel-form-open-proqa-buttons';
import { EventPanelFormOpenProQaSummaryButton } from './event-panel-form-open-proqa-summary-button';
import { Discipline } from './helpers/pro-qa-buttons-helpers';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { SESSIONS_INCIDENT_IDS } from './constants';

const EMPTY_INCIDENT_IDS = {
    POLICE: undefined,
    FIRE: undefined,
    EMS: undefined,
};

export function EventPanelFormProQaButtons({
    policeInstanceEnabled,
    fireInstanceEnabled,
    emsInstanceEnabled,
    enhancementsEnabled,
    isDraftEvent,
    setCurrentTempId,
}: {
    policeInstanceEnabled?: boolean;
    fireInstanceEnabled?: boolean;
    emsInstanceEnabled?: boolean;
    enhancementsEnabled?: boolean;
    isDraftEvent?: boolean;
    setCurrentTempId: (id?: string) => void;
}) {
    const enabledDisciplines = useMemo(() => {
        return Object.entries({
            POLICE: policeInstanceEnabled,
            FIRE: fireInstanceEnabled,
            EMS: emsInstanceEnabled,
        })
            .filter(([, enabled]) => enabled)
            .map(([discipline]) => discipline as Discipline);
    }, [policeInstanceEnabled, fireInstanceEnabled, emsInstanceEnabled]);

    const [temporaryIncidentIds, setTemporaryIncidentIds] = useState<
        Record<Discipline, string | undefined>
    >(() => {
        if (isDraftEvent) {
            return EMPTY_INCIDENT_IDS;
        }

        const stored = storageHelpers.getSessionItem(SESSIONS_INCIDENT_IDS);

        if (stored) {
            return stored as Record<Discipline, string | undefined>;
        }

        return EMPTY_INCIDENT_IDS;
    });

    const updateTemporaryIncidentId = useCallback(
        (discipline: Discipline, id: string | undefined) => {
            setTemporaryIncidentIds((prev) => {
                const next = { ...prev, [discipline]: id };
                storageHelpers.setSessionItem(SESSIONS_INCIDENT_IDS, next);
                return next;
            });
            setCurrentTempId(id);
        },
        [setCurrentTempId]
    );

    if (isDraftEvent && !enhancementsEnabled) {
        return null;
    }

    if (isDraftEvent || !enhancementsEnabled) {
        return (
            <EventPanelFormOpenProQaButtons
                enabledDisciplines={enabledDisciplines}
                enhancementsEnabled={enhancementsEnabled}
                temporaryIncidentIds={temporaryIncidentIds}
                updateTemporaryIncidentId={updateTemporaryIncidentId}
            />
        );
    }

    return (
        <HStack data-testid="proqa-buttons">
            <EventPanelFormOpenProQaButtons
                enabledDisciplines={enabledDisciplines}
                enhancementsEnabled={enhancementsEnabled}
                temporaryIncidentIds={temporaryIncidentIds}
                updateTemporaryIncidentId={updateTemporaryIncidentId}
            />
            <EventPanelFormOpenProQaSummaryButton enabledDisciplines={enabledDisciplines} />
        </HStack>
    );
}
